// eslint-disable-next-line jira/restricted/react-component-props
import React, { useMemo, type ComponentProps } from 'react';
import { graphql, useFragment } from 'react-relay';
import { expVal } from '@atlassian/jira-feature-experiments';
import VersionFilter from '@atlassian/jira-filters/src/ui/filters/version/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import type { versionFilterRelay_softwareFilters$key } from '@atlassian/jira-relay/src/__generated__/versionFilterRelay_softwareFilters.graphql';
import { UNASSIGNED_VERSION_ID } from '../../common/constants';
import messages from './messages';

export const VersionFilterRelay = ({
	versionFilterRelayFragmentRef,
	isUnassignedVersionOptionEnabled = false,
	...props
}: {
	versionFilterRelayFragmentRef: versionFilterRelay_softwareFilters$key;
	isUnassignedVersionOptionEnabled?: boolean;
} & Omit<ComponentProps<typeof VersionFilter>, 'versions'>) => {
	const { formatMessage } = useIntl();

	const edges = useFragment(
		graphql`
			fragment versionFilterRelay_softwareFilters on JiraVersionEdge @relay(plural: true) {
				node @required(action: THROW) {
					versionId @required(action: THROW)
					name @required(action: THROW)
					project {
						key
					}
				}
			}
		`,
		versionFilterRelayFragmentRef,
	);

	// Note: `version.id` from GraphQL represents a version ari
	// `version.versionId` represents the actual monolith numeric ID
	const versions = useMemo(() => {
		const transformedVersions = edges.map(({ node: { name, versionId: id, project } }) => ({
			id,
			name,
			projectKey: project?.key,
		}));

		if (isUnassignedVersionOptionEnabled) {
			transformedVersions.unshift({
				id: UNASSIGNED_VERSION_ID,
				name: formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.unassignedVersionNameIssueTermRefresh
						: messages.unassignedVersionName,
				),
				projectKey: undefined,
			});
		}

		return transformedVersions;
	}, [edges, formatMessage, isUnassignedVersionOptionEnabled]);

	const numVersions = versions.length;

	return (
		<ContextualAnalyticsData attributes={{ numVersions }}>
			<VersionFilter {...props} versions={versions} />
		</ContextualAnalyticsData>
	);
};
